import useSWR from "swr";

interface SubscriptionDetails {
  startDate: string;
  status: "incomplete" | "active" | "pending_cancel" | "canceled";
  paymentStatus: "paid" | "payment_failed";
  endDate: string | null;
  cancellationDate: string | null;
  endDateIfCancelNow: string;
  locationId: string;
  membership: {
    id: string;
    name: string;
    imgUrl: string;
    currency: string;
    membershipPlanId: string;
    timezone: string;
  };
  defaultPaymentMethod: {
    id: string;
    card: {
      brand: string;
      cardNetworkUrl: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
  } | null;
}

interface UseSubscriptionDetailsQueryReturn {
  data: SubscriptionDetails | undefined;
  isValidating: boolean;
  revalidate: () => Promise<boolean>;
}

export function useSubscriptionDetailsQuery(
  partnerId: string,
  subscriptionId: string
): UseSubscriptionDetailsQueryReturn {
  const { data, isValidating, revalidate } = useSWR(
    `/api/v3/portal/partners/${partnerId}/subscriptions/${subscriptionId}`
  );

  return { data: toSubscriptionDetails(data?.data), isValidating, revalidate };
}

function toSubscriptionDetails(data?: any): SubscriptionDetails | undefined {
  try {
    if (data) {
      const hasDefaultPaymentMethod = data.default_payment_method;

      return {
        startDate: data.start_date,
        status: data.status,
        paymentStatus: data.payment_status,
        endDate: data.end_date,
        cancellationDate: data.cancellation_date,
        endDateIfCancelNow: data.end_date_if_cancel_now,
        locationId: data.location_id,
        membership: {
          id: data.membership.id,
          name: data.membership.name,
          imgUrl: data.membership.images?.[0]?.xlarge.url,
          currency: data.membership.currency,
          membershipPlanId: data.membership.membership_plan_id,
          timezone: data.membership.time_zone,
        },
        defaultPaymentMethod: hasDefaultPaymentMethod
          ? {
              id: data.default_payment_method.id,
              card: {
                brand: data.default_payment_method.card.brand,
                cardNetworkUrl:
                  data.default_payment_method.card.card_network_url,
                last4: data.default_payment_method.card.last4,
                expMonth: data.default_payment_method.card.exp_month,
                expYear: data.default_payment_method.card.exp_year,
              },
            }
          : null,
      };
    }
  } catch {
    throw new Error("Error mapping data");
  }
}
